import { useEffect } from 'react';
import { useAccount, useConfig, useDisconnect } from 'wagmi';
import { watchAccount } from '@wagmi/core';
import useStore, { useAppConfigStore } from '../../../store/rootStore.ts';
import { getAddress } from 'viem';
import { telemetry } from '../../../utils/telemetry';

export function useWalletStateWatcher() {
  // TODO add email and social support
  const connectWallet = useStore((state) => state.connectWallet);
  const isAtLeastConnected = useStore((state) => state.isAtLeastConnected);
  const appAddress = useStore((state) => state.address);
  const logout = useStore((state) => state.logout);
  const status = useStore((state) => state.status);

  const configChain = useAppConfigStore().config?.chain;
  const configChainId = configChain?.id;

  // Wagmi state
  const {
    isConnected,
    isDisconnected,
    address: wagmiAddress,
    chainId: wagmiChainId,
    status: wagmiStatus,
    isReconnecting,
    isConnecting: isConnectPending,
  } = useAccount();
  const { disconnect: wagmiDisconnect, isPending: isDisconnectPending, isError: isDisconnectError } = useDisconnect();
  const wagmiConfig = useConfig();

  // Sync app state with Wagmi state
  // biome-ignore lint/correctness/useExhaustiveDependencies: isAtLeastConnected ignored to not self call
  useEffect(() => {
    // Wait for stable connection state
    if (isDisconnectPending || isConnectPending || isReconnecting) {
      return;
    }
    if (status === 'AUTHORITY_BLOCKED') {
      return;
    }

    if (isAtLeastConnected() || status === 'RESTRICTED') {
      // app thinks user logged in
      if (isDisconnected) {
        // Handle external disconnect
        // In Development (during HMR), Wagmi often reports disconnected briefly.
        // We skip strict logout in DEV to prevent session loss on code changes.
        if (import.meta.env.DEV) {
          console.warn('[HMR/DEV] Wallet disconnected state detected, but ignoring logout to preserve session.');
        } else {
          logout();
        }
      } else {
        // Verify address consistency
        if (appAddress !== wagmiAddress && wagmiAddress && wagmiChainId) {
          // Address mismatch: Force logout to clear old user data, then connect new wallet.
          logout();
          connectWallet('wallet', getAddress(wagmiAddress), configChainId ?? wagmiChainId);
          telemetry.logWalletConnection(getAddress(wagmiAddress), 'wallet', (configChainId ?? wagmiChainId).toString());
        }
      }
    } else {
      // App thinks user is logged out
      if (isConnected && wagmiAddress && wagmiChainId) {
        // Wallet already connected (e.g. persistent session), auto-connect app
        connectWallet('wallet', getAddress(wagmiAddress), configChainId ?? wagmiChainId);
        telemetry.logWalletConnection(getAddress(wagmiAddress), 'wallet', (configChainId ?? wagmiChainId).toString());
      } else {
        // wait for user actions
      }
    }
    return () => {};
  }, [
    isConnected,
    isDisconnected,
    isReconnecting,
    appAddress,
    wagmiAddress,
    wagmiChainId,
    configChainId,
    isDisconnectPending,
    isConnectPending,
    logout,
    connectWallet,
    status,
  ]);

  // Watch for real-time wallet events
  useEffect(() => {
    return watchAccount(wagmiConfig, {
      onChange(data) {
        const isSessionActive = isAtLeastConnected() || useStore.getState().status === 'RESTRICTED';
        // Address switch active during session
        if (isSessionActive && data.address && data.address !== appAddress && data.chainId) {
          // Logout to clear old data, then connect new wallet
          useStore.getState().logout();

          const targetChainId = useStore.getState().config?.chain.id ?? data.chainId;
          connectWallet('wallet', getAddress(data.address), targetChainId);
          telemetry.logWalletConnection(getAddress(data.address), 'wallet', targetChainId.toString());
        } else if (isSessionActive && data.status === 'disconnected') {
          logout();
        }
      },
    });
  }, [appAddress, logout, isAtLeastConnected, connectWallet, wagmiConfig]);

  useEffect(() => {
    if (isDisconnectError) {
      console.error('Wallet error:', wagmiStatus); // use notification
    }
  }, [isDisconnectError, wagmiStatus]);
}
